import React from 'react';
import BuildsNoDataPlaceholder from '../BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import Heading from '../Heading/Heading';
import { EnhancedMarkdownOptionsContextProvider } from '@resources/js/contexts/useEnhancedMarkdownOptions';
import VerticalContent from '@resources/js/components/VerticalContent/VerticalContent';
import Body from '@resources/js/components/Body/Body';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';
import DefaultLinkWrapper, {
  LinkWrapper,
} from '@resources/js/components/LinkWrapper/LinkWrapper';

export type BuildsGearPiece = {
  icon: string;
  parses: string;
  popularity: string;
};

export type BuildsBestInSlotGearSectionProps = {
  title: string;
  description: string;
  link?: { label: string; url: string };
  itemLabel: string;
  popularityLabel: string;
  gear: BuildsGearPiece[];
  weaponsLabel: string;
  weapons: BuildsGearPiece[];
  trinketsLabel: string;
  trinkets: BuildsGearPiece[];
  LinkWrapper?: LinkWrapper;
};

export default function BuildsBestInSlotGearSection({
  title,
  description,
  link,
  gear,
  weapons,
  trinkets,
  trinketsLabel,
  weaponsLabel,
  itemLabel,
  popularityLabel,
  LinkWrapper = DefaultLinkWrapper,
}: BuildsBestInSlotGearSectionProps): JSX.Element {
  const noData = gear.length === 0;
  return (
    <EnhancedMarkdownOptionsContextProvider value={{ lazyLoad: false }}>
      <VerticalContent
        className='builds-best-in-slot-gear-section'
        gap={'small'}
      >
        <Body>
          <div className={'builds-best-in-slot-gear-section__heading-and-link'}>
            <Heading level={2}>
              <EnhancedMarkdown markdown={title} wrapped={false} />
            </Heading>
            {link && <LinkWrapper href={link.url}>{link.label}</LinkWrapper>}
          </div>
          <EnhancedMarkdown
            markdown={description}
            wrapped={false}
            overrides={buildsEnhancedMarkdownOverrides}
          />
        </Body>

        {noData ? (
          <BuildsNoDataPlaceholder marginTop={1} />
        ) : (
          <div>
            <div>
              <div className='builds-best-in-slot-gear-section__header'>
                <div className='builds-best-in-slot-gear-section__header-item'>
                  <div>{itemLabel}</div>
                  <div>{popularityLabel}</div>
                </div>
                <div className='builds-best-in-slot-gear-section__header-item'>
                  <div>{itemLabel}</div>
                  <div>{popularityLabel}</div>
                </div>
              </div>

              <div className='builds-best-in-slot-gear-section__gear'>
                {gear.map((item, index) => {
                  return <GearPiece item={item} key={index} />;
                })}
              </div>
            </div>
            <div className='builds-best-in-slot-gear-section__highlighted-gear'>
              <div className='builds-best-in-slot-gear-section__highlighted-gear-title-container'>
                <Heading
                  className='builds-best-in-slot-gear-section__highlighted-gear-title'
                  level={3}
                >
                  {weaponsLabel}{' '}
                  <span className='builds-best-in-slot-gear-section__highlighted-gear-title--mobile'>
                    {' '}
                    & {trinketsLabel}
                  </span>
                </Heading>
                <Heading
                  className='builds-best-in-slot-gear-section__highlighted-gear-title'
                  level={3}
                >
                  {trinketsLabel}
                </Heading>
              </div>

              <div className='builds-best-in-slot-gear-section__gear'>
                {weapons.map((item, index) => {
                  return (
                    <GearPiece
                      column={1}
                      row={index + 1}
                      item={item}
                      key={index}
                    />
                  );
                })}

                {trinkets.map((item, index) => {
                  return (
                    <GearPiece
                      column={2}
                      row={index + 1}
                      item={item}
                      key={weapons.length + index}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </VerticalContent>
    </EnhancedMarkdownOptionsContextProvider>
  );
}

type GearPieceProps = {
  row?: number;
  column?: number;
  item: BuildsGearPiece;
};

function GearPiece({ row, column, item }: GearPieceProps): JSX.Element {
  return (
    <div
      className='builds-best-in-slot-gear-section__gear-item'
      data-column={column}
      data-row={row}
    >
      <div className='builds-best-in-slot-gear-section__gear-item-icon'>
        <EnhancedMarkdown markdown={item.icon} wrapped={false} />
      </div>
      <div className='builds-best-in-slot-gear-section__gear-item-meta'>
        <div className='builds-best-in-slot-gear-section__gear-item-meta-popularity'>
          <EnhancedMarkdown markdown={item.popularity} wrapped={false} />
        </div>
        <div className='builds-best-in-slot-gear-section__gear-item-meta-parses'>
          {item.parses}
        </div>
      </div>
    </div>
  );
}
